import React, { useState } from "react";
import { Tabs, Popconfirm, Button } from "antd";
import { Plus, Edit2, Trash2 } from "lucide-react";
import DataTable from "../../components/DataTable";
import CountryModal from "./CountryModal";
import StateModal from "./StateModal";
import CityModal from "./CityModal";
import {
  useCountryList,
  useCountryDelete,
  useStateList,
  useStateDelete,
  useCityList,
  useCityDelete,
} from "../../../hooks/api/location/hooks";
import { ICountry, IState, ICity } from "../../../types/api/location";

import { useMessageApi } from "../../../provider/AntdNotification";

const LocationPanel: React.FC = () => {
  const messageApi = useMessageApi();
  const [activeTab, setActiveTab] = useState("country");

  // Country State
  const [qDataCountry, setQDataCountry] = useState<{ page: number; limit: number; search?: string }>({ page: 1, limit: 10 });
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);

  // State State
  const [qDataState, setQDataState] = useState<{ page: number; limit: number; search?: string }>({ page: 1, limit: 10 });
  const [isStateModalOpen, setIsStateModalOpen] = useState(false);
  const [selectedState, setSelectedState] = useState<IState | null>(null);

  // City State
  const [qDataCity, setQDataCity] = useState<{ page: number; limit: number; search?: string }>({ page: 1, limit: 10 });
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<ICity | null>(null);

  // Queries
  const { data: countriesData, isFetching: isFetchingCountries } = useCountryList(qDataCountry, { enabled: activeTab === "country" });
  const deleteCountry = useCountryDelete({
    qData: qDataCountry,
    onSuccess: () => messageApi.success("Country deleted successfully"),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to delete country"),
  });

  const { data: statesData, isFetching: isFetchingStates } = useStateList(qDataState, { enabled: activeTab === "state" });
  const deleteState = useStateDelete({
    qData: qDataState,
    onSuccess: () => messageApi.success("State deleted successfully"),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to delete state"),
  });

  const { data: citiesData, isFetching: isFetchingCities } = useCityList(qDataCity, { enabled: activeTab === "city" });
  const deleteCity = useCityDelete({
    qData: qDataCity,
    onSuccess: () => messageApi.success("City deleted successfully"),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to delete city"),
  });

  const renderCountryTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Countries</h1>
          <p className="text-sm text-slate-500 mt-1">Manage countries in the system</p>
        </div>
        <button
          onClick={() => {
            setSelectedCountry(null);
            setIsCountryModalOpen(true);
          }}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover transition-colors"
        >
          <Plus size={20} />
          <span>Add Country</span>
        </button>
      </div>
      <DataTable
        columns={[
          { title: "Name", dataIndex: "name", key: "name" },
          { title: "Slug", dataIndex: "slug", key: "slug" },
          {
            title: "Sculptures",
            dataIndex: "productCount",
            key: "productCount",
            render: (count: number) => (
              <span className={count ? "text-slate-700" : "text-slate-400"}>
                {count || 0}
              </span>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: boolean) => (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {status ? "Active" : "Inactive"}
              </span>
            ),
          },
          {
            title: "Action",
            key: "action",
            render: (_: any, record: ICountry) => (
              <div className="flex items-center gap-2">
                <Button
                  type="text"
                  icon={<Edit2 size={16} />}
                  onClick={() => {
                    setSelectedCountry(record);
                    setIsCountryModalOpen(true);
                  }}
                  className="text-blue-600 hover:bg-blue-50"
                />
                <Popconfirm
                  title="Delete Country"
                  description="Are you sure you want to delete this country?"
                  onConfirm={() => deleteCountry.mutate(record._id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true, loading: deleteCountry.isPending }}
                >
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    className="hover:bg-red-50"
                  />
                </Popconfirm>
              </div>
            ),
          }
        ]}
        dataSource={countriesData?.data || []}
        loading={isFetchingCountries}
        paginationProps={{
          total: countriesData?.extra?.total || 0,
          page: qDataCountry.page,
          limit: qDataCountry.limit,
          onChange: (page, limit) => setQDataCountry({ ...qDataCountry, page, limit }),
        }}
        searchProps={{
          searchValue: qDataCountry.search || "",
          onSearchChange: (search) => setQDataCountry({ ...qDataCountry, search, page: 1 }),
          placeholder: "Search countries...",
        }}
      />
    </div>
  );

  const renderStateTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">States</h1>
          <p className="text-sm text-slate-500 mt-1">Manage states and provinces</p>
        </div>
        <button
          onClick={() => {
            setSelectedState(null);
            setIsStateModalOpen(true);
          }}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover transition-colors"
        >
          <Plus size={20} />
          <span>Add State</span>
        </button>
      </div>
      <DataTable
        columns={[
          { title: "Name", dataIndex: "name", key: "name" },
          {
            title: "Country",
            dataIndex: "countryId",
            key: "countryId",
            render: (countryId: any) => countryId?.name || "N/A",
          },
          { title: "Slug", dataIndex: "slug", key: "slug" },
          {
            title: "Sculptures",
            dataIndex: "productCount",
            key: "productCount",
            render: (count: number) => (
              <span className={count ? "text-slate-700" : "text-slate-400"}>
                {count || 0}
              </span>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: boolean) => (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {status ? "Active" : "Inactive"}
              </span>
            ),
          },
          {
            title: "Action",
            key: "action",
            render: (_: any, record: IState) => (
              <div className="flex items-center gap-2">
                <Button
                  type="text"
                  icon={<Edit2 size={16} />}
                  onClick={() => {
                    setSelectedState(record);
                    setIsStateModalOpen(true);
                  }}
                  className="text-blue-600 hover:bg-blue-50"
                />
                <Popconfirm
                  title="Delete State"
                  description="Are you sure you want to delete this state?"
                  onConfirm={() => deleteState.mutate(record._id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true, loading: deleteState.isPending }}
                >
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    className="hover:bg-red-50"
                  />
                </Popconfirm>
              </div>
            ),
          }
        ]}
        dataSource={statesData?.data || []}
        loading={isFetchingStates}
        paginationProps={{
          total: statesData?.extra?.total || 0,
          page: qDataState.page,
          limit: qDataState.limit,
          onChange: (page, limit) => setQDataState({ ...qDataState, page, limit }),
        }}
        searchProps={{
          searchValue: qDataState.search || "",
          onSearchChange: (search) => setQDataState({ ...qDataState, search, page: 1 }),
          placeholder: "Search states...",
        }}
      />
    </div>
  );

  const renderCityTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Cities</h1>
          <p className="text-sm text-slate-500 mt-1">Manage cities</p>
        </div>
        <button
          onClick={() => {
            setSelectedCity(null);
            setIsCityModalOpen(true);
          }}
          className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-hover transition-colors"
        >
          <Plus size={20} />
          <span>Add City</span>
        </button>
      </div>
      <DataTable
        columns={[
          { title: "Name", dataIndex: "name", key: "name" },
          {
            title: "State",
            dataIndex: "stateId",
            key: "stateId",
            render: (stateId: any) => stateId?.name || "N/A",
          },
          {
            title: "Country",
            dataIndex: "countryId",
            key: "countryId",
            render: (countryId: any) => countryId?.name || "N/A",
          },
          {
            title: "Sculptures",
            dataIndex: "productCount",
            key: "productCount",
            render: (count: number) => (
              <span className={count ? "text-slate-700" : "text-slate-400"}>
                {count || 0}
              </span>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status: boolean) => (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {status ? "Active" : "Inactive"}
              </span>
            ),
          },
          {
            title: "Action",
            key: "action",
            render: (_: any, record: ICity) => (
              <div className="flex items-center gap-2">
                <Button
                  type="text"
                  icon={<Edit2 size={16} />}
                  onClick={() => {
                    setSelectedCity(record);
                    setIsCityModalOpen(true);
                  }}
                  className="text-blue-600 hover:bg-blue-50"
                />
                <Popconfirm
                  title="Delete City"
                  description="Are you sure you want to delete this city?"
                  onConfirm={() => deleteCity.mutate(record._id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true, loading: deleteCity.isPending }}
                >
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    className="hover:bg-red-50"
                  />
                </Popconfirm>
              </div>
            ),
          }
        ]}
        dataSource={citiesData?.data || []}
        loading={isFetchingCities}
        paginationProps={{
          total: citiesData?.extra?.total || 0,
          page: qDataCity.page,
          limit: qDataCity.limit,
          onChange: (page, limit) => setQDataCity({ ...qDataCity, page, limit }),
        }}
        searchProps={{
          searchValue: qDataCity.search || "",
          onSearchChange: (search) => setQDataCity({ ...qDataCity, search, page: 1 }),
          placeholder: "Search cities...",
        }}
      />
    </div>
  );

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: "country", label: "Countries", children: renderCountryTab() },
            { key: "state", label: "States", children: renderStateTab() },
            { key: "city", label: "Cities", children: renderCityTab() },
          ]}
        />
      </div>

      <CountryModal
        isOpen={isCountryModalOpen}
        onClose={() => setIsCountryModalOpen(false)}
        selectedItem={selectedCountry}
        qData={qDataCountry}
      />

      <StateModal
        isOpen={isStateModalOpen}
        onClose={() => setIsStateModalOpen(false)}
        selectedItem={selectedState}
        qData={qDataState}
      />

      <CityModal
        isOpen={isCityModalOpen}
        onClose={() => setIsCityModalOpen(false)}
        selectedItem={selectedCity}
        qData={qDataCity}
      />
    </div>
  );
};

export default LocationPanel;
